import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import RouteMap from '../components/routes/RouteMap';
import ShadowTimeline, { ShadowLegend } from '../components/shadow/ShadowTimeline';
import ShadowShiftSummary from '../components/shadow/ShadowShiftSummary';
import ShadowDriverStops from '../components/shadow/ShadowDriverStops';
import { Button } from '../components/ui/Button';
import { colouredDrivers, planLine, reasonLabel, staleMinutes, summaryLine, toRouteMap } from '../components/shadow/shadowPlanView';
import { shadowPlansApi } from '../services/shadowPlans.api';
import { Select } from '../components/ui/Select';
import { getBaghdadOperationalDate } from '../utils/pickupSlotStatus';

type View = 'latest' | 'morning';

export default function ShadowPlanPage() {
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [selected, setSelected] = useState<string>();

  const { data: dates = [], isLoading: datesLoading, isError: datesError } = useQuery({ queryKey: ['shadow-dates'], queryFn: shadowPlansApi.listDates });
  const sorted = useMemo(() => [...dates].sort().reverse(), [dates]);
  const today = getBaghdadOperationalDate();
  const date = pickedDate ?? (sorted.includes(today) ? today : sorted[0]);

  const { data: day, isLoading, isError } = useQuery({
    queryKey: ['shadow-day', date],
    queryFn: () => shadowPlansApi.getDay(date),
    enabled: !!date,
    refetchInterval: date === today ? 60_000 : false,
  });

  // Until ops pick a view: open on the morning plan while today has no update yet.
  const shown: View = view ?? (date === today && day && !day.latest && day.morning ? 'morning' : 'latest');
  const plan = day ? (shown === 'latest' ? day.latest : day.morning) : null;
  const stale = date === today && day ? staleMinutes(day.latest ?? day.morning, new Date()) : null;
  const drivers = useMemo(() => (plan ? colouredDrivers(plan) : []), [plan]);
  const map = useMemo(() => (plan ? toRouteMap(drivers) : null), [plan, drivers]);
  const chosen = drivers.find((d) => d.label === selected);

  let body;
  if (isLoading || datesLoading) body = <p className="text-sm text-gray-500">Loading...</p>;
  else if ((isError && !day) || datesError) body = <p className="text-sm text-red-600">Failed to load the shadow plan</p>;
  else if (!date || (day && !day.morning && !day.latest)) body = <p className="py-8 text-center text-[13px] text-gray-500">No shadow plan for this day</p>;
  else if (!plan || !map) body = <p className="py-8 text-center text-[13px] text-gray-500">{shown === 'latest' ? (date === today ? 'No update yet today' : 'No update for this day') : 'No morning plan for this day'}</p>;
  else
    body = (
      <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-800 tabular-nums">{planLine(plan)}</p>
        <p className="text-xs text-gray-500">Each row is one planned van, not a real driver. Click a driver to see their route and stops.</p>
      </div>
      <ShadowShiftSummary plan={plan} />
      <ShadowLegend kinds={new Set(drivers.flatMap((d) => d.stops.map((s) => s.kind)))} />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="min-w-0 space-y-3">
          <ShadowTimeline drivers={drivers} opDate={plan.opDate} selectedLabel={selected} onSelect={setSelected} />
          {plan.unassigned.length > 0 && (
            <div className="text-xs text-gray-600">
              <p className="font-medium text-gray-700">Not assigned ({plan.unassigned.length})</p>
              <ul className="mt-1 space-y-0.5">
                {plan.unassigned.map((u) => (
                  <li key={u.key}>
                    <span dir="auto">{u.merchantId != null ? `Merchant ${u.merchantId}` : u.key}</span> · {reasonLabel(u.reason)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="min-w-0 space-y-2">
          <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
            <span>{chosen ? `Showing Driver ${chosen.label} only` : `Showing all ${drivers.length} drivers. Line colour = driver, number = stop order`}</span>
            {chosen && (
              <Button size="sm" variant="outline" onClick={() => setSelected(undefined)}>
                Show all drivers
              </Button>
            )}
          </div>
          <RouteMap routes={chosen ? map.routes.filter((r) => r._id === chosen.label) : map.routes} stops={map.stops} selectedRouteId={selected} className="h-[480px] rounded-lg" />
        </div>
      </div>
      {chosen && <ShadowDriverStops driver={chosen} />}
      </div>
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shadow plan</CardTitle>
        <p className="text-sm text-gray-500">Suggested plan only. No route, task or driver is changed.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select
            aria-label="Date"
            size="sm"
            value={date ?? ''}
            onChange={(e) => setPickedDate(e.target.value)}
            options={sorted.map((d) => ({ value: d, label: d }))}
          />
          <SegmentedControl
            label="Plan"
            value={shown}
            onChange={setView}
            options={[{ value: 'latest', label: 'Plan now' }, { value: 'morning', label: 'Morning plan' }]}
          />
          {day && <span className="text-sm text-gray-600 tabular-nums">{summaryLine(day.morning, day.latest)}</span>}
        </div>
        {stale != null && (
          <p role="alert" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Warning: this plan is {stale} minutes old. Updates should come every 15 minutes, so the planner may be stuck.
          </p>
        )}
        {body}
      </CardContent>
    </Card>
  );
}
